import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    RoleItem
} from "./type";
import stylesheet from "./stylesheet";
import RoleFormModal from "./views/roleFormModal";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    NCoreUIKitModal,
    PageContainer,
    Button,
    Text
} from "ncore-ui-kit";
import {
    RefreshCcwIcon,
    ShieldIcon,
    PencilIcon,
    TrashIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const Roles = () => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        roles,
        setRoles
    ] = useState<RoleItem[]>([]);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    const fetchRoles = useCallback(async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");
            const response = await fetch(`${process.env.API_URL}/roles`, {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setRoles(result.data);
            }
        } catch (error) {
            console.error("Roller alınamadı:", error);
        }
    }, [
        activeOrganizationId,
        user?.role
    ]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const handleOpenCreateModal = () => {
        NCoreUIKitModal.open({
            children: (
                <RoleFormModal
                    modalId="role-form-modal"
                    onSuccess={fetchRoles}
                />),
            id: "role-form-modal",
            onOverlayPress: ({
                closeAnimation
            }) => closeAnimation()
        });
    };

    const handleOpenEditModal = (item: RoleItem) => {
        NCoreUIKitModal.open({
            children: (
                <RoleFormModal
                    modalId="role-form-modal"
                    onSuccess={fetchRoles}
                    initialData={item}
                />),
            id: "role-form-modal",
            onOverlayPress: ({
                closeAnimation
            }) => closeAnimation()
        });
    };

    const handleDelete = (id: string, name: string, force: boolean = false) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const url = new URL(`${process.env.API_URL}/roles/${id}`);

                        if (force) {
                            url.searchParams.append("force", "true");
                        }

                        if (activeOrganizationId && user?.role === "root") {
                            url.searchParams.append("organization_id", activeOrganizationId);
                        }

                        const response = await fetch(url.toString(), {
                            headers: {
                                "Content-Type": "application/json",
                                ...(activeOrganizationId && user?.role === "root" ? {
                                    "x-organization-id": activeOrganizationId
                                } : {}),
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });

                        const result = await response.json();

                        if (response.status === 409) {
                            closeAnimation(() => {
                                setTimeout(() => {
                                    NCoreUIKitDialog.open({
                                        primaryButtonProps: {
                                            title: localize("roles-dialog-delete-force-primary"),
                                            onPress: ({
                                                closeAnimation: closeForce
                                            }) => {
                                                closeForce(() => handleDelete(id, name, true));
                                            },
                                            type: "danger"
                                        },
                                        content: localize("roles-dialog-delete-force-content", [
                                            result.message
                                        ]),
                                        title: localize("roles-dialog-delete-force-title"),
                                        secondaryButtonProps: {
                                            title: localize("roles-dialog-delete-cancel"),
                                            onPress: ({
                                                closeAnimation: closeForce
                                            }) => closeForce(),
                                            type: "neutral"
                                        },
                                        variant: "yes-no",
                                        isVisible: true
                                    });
                                }, 300);
                            });
                            return;
                        }

                        if (result.status === "success") {
                            NCoreUIKitToast.open({
                                title: localize("roles-toast-delete-success")
                            });
                            fetchRoles();
                        } else {
                            alert(result.message || (result.errors ? result.errors.map((e: { message: string }) => e.message).join(", ") : localize("roles-toast-unknown-error")));
                        }
                    } catch (error) {
                        console.error("Silme hatası:", error);
                    } finally {
                        isDeleting = false;

                        closeAnimation();
                    }
                },
                title: localize("roles-dialog-delete-primary"),
                type: "danger"
            },
            content: localize("roles-dialog-delete-content", [
                name
            ]),
            secondaryButtonProps: {
                title: localize("roles-dialog-delete-cancel"),
                onPress: ({
                    closeAnimation
                }) => closeAnimation(),
                type: "neutral"
            },
            title: localize("roles-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("roles-page-title")}
                </Text>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchRoles();
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("roles-page-add-button")}
                        onPress={handleOpenCreateModal}
                    />
                </View>
            </View>
            <ScrollView
                contentContainerStyle={[
                    stylesheet.listContent,
                    {
                        paddingBottom: spaces.spacingXl,
                        gap: spaces.spacingMd
                    }
                ]}
                showsVerticalScrollIndicator={false}
            >
                {roles.map((item) => {
                    return <TouchableOpacity
                        style={[
                            stylesheet.itemContainer,
                            {
                                backgroundColor: colors.content.container.subtle,
                                alignItems: isMobile ? "stretch" : "center",
                                flexDirection: isMobile ? "column" : "row",
                                borderRadius: radiuses.md,
                                padding: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                        activeOpacity={0.7}
                        key={item.id}
                    >
                        <View
                            style={[
                                stylesheet.itemContent,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.itemIcon,
                                    {
                                        backgroundColor: colors.content.container.default,
                                        borderRadius: radiuses.full
                                    }
                                ]}
                            >
                                <ShieldIcon
                                    color={colors.content.icon.emphasized}
                                    size={24}
                                />
                            </View>
                            <View
                                style={[
                                    stylesheet.itemInfo,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {item.role_name}
                                </Text>
                                <View
                                    style={[
                                        stylesheet.permissionsContainer,
                                        {
                                            gap: spaces.spacingSm
                                        }
                                    ]}
                                >
                                    {item.permissions.map((p, index) => {
                                        return <View
                                            style={[
                                                stylesheet.permissionBadge,
                                                {
                                                    backgroundColor: colors.content.container.default,
                                                    paddingHorizontal: spaces.spacingSm,
                                                    paddingVertical: spaces.spacingXs,
                                                    borderRadius: radiuses.sm
                                                }
                                            ]}
                                            key={index}
                                        >
                                            <Text
                                                variant="bodySmallSize"
                                                color="mid"
                                            >
                                                {localize(`roles-perm-${p.module}-${p.action}` as Parameters<typeof localize>[0])}
                                            </Text>
                                        </View>;
                                    })}
                                </View>
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.itemActions,
                                {
                                    alignItems: isMobile ? "stretch" : "center",
                                    flexDirection: isMobile ? "column" : "row",
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => {
                                    return <PencilIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />;
                                }}
                                title={localize("Düzenle" as Parameters<typeof localize>[0])}
                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                onPress={() => handleOpenEditModal(item)}
                                type="neutral"
                            />
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => {
                                    return <TrashIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />;
                                }}
                                onPress={() => handleDelete(item.id, item.role_name)}
                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                title={localize("roles-list-delete-button")}
                                type="danger"
                            />
                        </View>
                    </TouchableOpacity>;
                })}
            </ScrollView>
        </View>
    </PageContainer>;
};
export default Roles;
